import { invalidateNutritionQueries } from "@/lib/nutritionQueries";
import { useRef, useState, type ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { Camera, Loader2, RotateCcw, AlertCircle } from "lucide-react";
import { api } from "@/api/client";
import { Card, SectionLabel } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import type { MealEntry } from "@/types";

interface Analysis {
  description?: string;
  items?: Array<{
    name: string;
    estimated_grams?: number;
    calories?: number;
    protein_g?: number;
    carbs_g?: number;
    fat_g?: number;
  }>;
  total_calories?: number;
  total_protein_g?: number;
  total_carbs_g?: number;
  total_fat_g?: number;
  total_fiber_g?: number;
  confidence?: number;
  notes?: string;
  warnings?: string[];
  error?: string;
}

async function compressImage(file: File, maxSide = 1024, quality = 0.85): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      let { width, height } = img;
      if (width > height && width > maxSide) {
        height = Math.round((height * maxSide) / width);
        width = maxSide;
      } else if (height > maxSide) {
        width = Math.round((width * maxSide) / height);
        height = maxSide;
      }
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) return reject(new Error("no canvas ctx"));
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error("toBlob failed"))),
        "image/jpeg",
        quality,
      );
    };
    img.onerror = () => reject(new Error("image load failed"));
    img.src = URL.createObjectURL(file);
  });
}

export function PhotoMealUpload() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [meal, setMeal] = useState<MealEntry | null>(null);
  const [analysis, setAnalysis] = useState<Analysis | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setPreview(null);
    setMeal(null);
    setAnalysis(null);
    setError(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleFile = async (e: ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const compressed = await compressImage(f, 1024, 0.85);
      const reader = new FileReader();
      reader.onload = (ev) => setPreview(ev.target?.result as string);
      reader.readAsDataURL(compressed);

      setAnalyzing(true);
      const form = new FormData();
      form.append("photo", compressed, "meal.jpg");
      const { data } = await api.post("/nutrition/meals/from-photo", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMeal(data);
      setAnalysis(
        (data as { ai_analysis_raw?: Analysis }).ai_analysis_raw || {
          total_calories: data.calories,
          total_protein_g: data.protein_g,
          total_carbs_g: data.carbs_g,
          total_fat_g: data.fat_g,
          confidence: data.ai_confidence,
        },
      );
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["nutrition-meals"] }),
        invalidateNutritionQueries(qc),
        qc.invalidateQueries({ queryKey: ["dashboard"] }),
      ]);
    } catch (err: any) {
      const detail =
        err?.response?.status === 413
          ? t("common.upload_too_large")
          : err?.response?.data?.detail || err?.message || t("meal_detail.analyze_failed");
      setError(detail);
    } finally {
      setAnalyzing(false);
    }
  };

  if (meal && analysis) {
    return (
      <Card>
        <div className="flex items-center justify-between">
          <SectionLabel>{t("photo_upload.recognized_confidence", { pct: Math.round((analysis.confidence || 0) * 100) })}</SectionLabel>
          <button
            onClick={reset}
            className="inline-flex items-center gap-1 text-[11px] text-taupe hover:text-navy-deep"
          >
            <RotateCcw className="h-3 w-3" strokeWidth={1.8} />
            {t("photo_upload.another_photo")}
          </button>
        </div>
        {preview ? (
          <img
            src={preview}
            alt="meal"
            className="mt-3 max-h-[260px] w-full rounded-lg object-contain"
          />
        ) : null}
        <h4 className="mt-3 font-medium text-navy-deep">{analysis.description}</h4>
        <div className="mt-3 grid grid-cols-4 gap-2 text-center text-[11px]">
          <Box label={t("photo_upload.kcal_label")} value={analysis.total_calories ?? 0} />
          <Box label={t("photo_upload.protein_label")} value={`${analysis.total_protein_g ?? 0}g`} />
          <Box label={t("photo_upload.carbs_label")} value={`${analysis.total_carbs_g ?? 0}g`} />
          <Box label={t("photo_upload.fat_label")} value={`${analysis.total_fat_g ?? 0}g`} />
        </div>
        {analysis.items && analysis.items.length > 0 ? (
          <details className="mt-3">
            <summary className="cursor-pointer text-[11px] uppercase tracking-wider text-taupe hover:text-navy-deep">
              {t("photo_upload.details_n_items", { n: analysis.items.length })}
            </summary>
            <ul className="mt-2 space-y-1 text-[12px]">
              {analysis.items.map((it, i) => (
                <li
                  key={i}
                  className="flex justify-between border-b border-cream-border/50 py-1"
                >
                  <span className="text-navy-deep">
                    {it.name}
                    {it.estimated_grams ? ` · ${it.estimated_grams}${t("nutrition_page.g")}` : ""}
                  </span>
                  <span className="text-taupe">
                    {it.calories ?? 0} {t("nutrition_archive.kcal")}
                  </span>
                </li>
              ))}
            </ul>
          </details>
        ) : null}
        {analysis.notes ? (
          <p className="mt-3 text-[12px] italic text-taupe">{analysis.notes}</p>
        ) : null}
        {analysis.warnings && analysis.warnings.length > 0 ? (
          <p className="mt-2 flex items-center gap-1 text-[12px] text-coral-dark">
            <AlertCircle className="h-3.5 w-3.5" strokeWidth={2} />
            {analysis.warnings.join(", ")}
          </p>
        ) : null}
        <p className="mt-4 text-[11px] text-taupe">
          Сохранено в журнал · {meal.source}.{" "}
          Нужно подправить? Удали в списке ниже и занеси руками.
        </p>
      </Card>
    );
  }

  return (
    <Card>
      <SectionLabel>{t("photo_upload.section_label")}</SectionLabel>
      <p className="mt-1 text-[12px] text-taupe">{t("photo_upload.subtitle")}</p>
      {preview && analyzing ? (
        <div className="mt-3">
          <img
            src={preview}
            alt="meal"
            className="max-h-[260px] w-full rounded-lg object-contain opacity-70"
          />
          <div className="mt-3 inline-flex items-center gap-2 text-[13px] text-taupe">
            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.8} />
            {t("photo_upload.analyzing")}
          </div>
        </div>
      ) : (
        <label className="mt-3 flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-cream-border bg-cream-light/40 p-6 text-center hover:border-amber/60">
          <Camera className="h-6 w-6 text-taupe" strokeWidth={1.6} />
          <span className="mt-2 text-[13px] font-medium text-navy-deep">{t("photo_upload.drop_label")}</span>
          <span className="mt-0.5 text-[11px] text-taupe">{t("photo_upload.drop_hint")}</span>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFile}
            className="hidden"
          />
        </label>
      )}
      {error ? (
        <p className="mt-3 flex items-center gap-1 text-[12px] text-coral-dark">
          <AlertCircle className="h-3.5 w-3.5" strokeWidth={2} />
          {error}
        </p>
      ) : null}
    </Card>
  );
}

function Box({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded bg-cream-light px-2 py-2">
      <div className="text-[9px] uppercase tracking-wider text-taupe">{label}</div>
      <div className="metric-number mt-0.5 text-[15px] text-navy-deep">{value}</div>
    </div>
  );
}
